import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, X } from 'lucide-react';

type ProntuarioUnsavedChangesModalProps = {
	isOpen: boolean;
	isSaving: boolean;
	onSaveAndExit: () => void;
	onExitWithoutSaving: () => void;
	onCancel: () => void;
};

const ProntuarioUnsavedChangesModal = ({
	isOpen,
	isSaving,
	onSaveAndExit,
	onExitWithoutSaving,
	onCancel,
}: ProntuarioUnsavedChangesModalProps) => {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[10030] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={isSaving ? undefined : onCancel}
				aria-hidden
			/>
			<div
				className="animate-in zoom-in-95 relative w-full max-w-[400px] rounded-[2rem] bg-white p-8 pt-10 text-center shadow-2xl duration-200"
				role="dialog"
				aria-modal="true"
				aria-labelledby="prontuario-unsaved-title"
			>
				<button
					type="button"
					onClick={onCancel}
					disabled={isSaving}
					className="absolute right-4 top-4 flex size-9 items-center justify-center rounded-full bg-zinc-100 text-zinc-600 transition-colors hover:bg-zinc-200 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
					aria-label="Cancelar"
				>
					<X size={18} />
				</button>

				<div className="mb-4 flex justify-center text-amber-500">
					<div className="rounded-full bg-amber-50 p-4">
						{isSaving ? (
							<Loader2
								size={32}
								strokeWidth={2.5}
								aria-hidden
								className="animate-spin"
							/>
						) : (
							<AlertTriangle
								size={32}
								strokeWidth={2.5}
								aria-hidden
							/>
						)}
					</div>
				</div>

				<h3
					id="prontuario-unsaved-title"
					className="mb-2 text-xl font-black text-stone-900"
				>
					Alterações não salvas
				</h3>

				<p className="mb-8 text-sm font-medium leading-relaxed text-stone-500">
					{isSaving
						? 'Salvando alterações desta sessão...'
						: 'Você tem alterações nesta sessão. O que deseja fazer?'}
				</p>

				<div className="flex flex-col gap-3">
					<button
						type="button"
						onClick={onSaveAndExit}
						disabled={isSaving}
						className="flex w-full items-center justify-center rounded-full border-none bg-zinc-900 py-3 font-bold text-white shadow-md transition-colors hover:bg-zinc-800 disabled:cursor-default disabled:opacity-60"
					>
						{isSaving ? 'Salvando...' : 'Salvar e voltar'}
					</button>

					<button
						type="button"
						onClick={onExitWithoutSaving}
						disabled={isSaving}
						className="text-sm font-bold text-stone-600 transition-colors hover:text-stone-900 disabled:opacity-50"
					>
						Voltar sem salvar
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};

export { ProntuarioUnsavedChangesModal };
